import { useState, useRef, useEffect, useCallback } from "react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Sparkles, Send, X, MessageSquarePlus, History } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const SUGGESTED = [
  { label: "Plan my day",              endpoint: "plan" },
  { label: "What needs me today?",     endpoint: "ask"  },
  { label: "How's my budget?",         endpoint: "ask"  },
  { label: "How much have I spent on dining?", endpoint: "ask" },
  { label: "What's coming up?",        endpoint: "ask"  },
  { label: "What can I let go?",       endpoint: "ask"  },
];

const LS_SESSION = "aevum_brain_session_v1";

export default function BrainPanel({ open, onOpenChange }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(LS_SESSION));
  const [sessions, setSessions] = useState([]);
  const scrollRef = useRef(null);

  // On open: load past messages for saved session + list of sessions
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const s = await api.get("/brain/sessions");
        setSessions(s.data || []);
      } catch { setSessions([]); }
      if (sessionId) {
        try {
          const r = await api.get(`/brain/sessions/${sessionId}/messages`);
          setMessages(r.data || []);
        } catch { setMessages([]); }
      }
    })();
  }, [open, sessionId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const newChat = () => {
    localStorage.removeItem(LS_SESSION);
    setSessionId(null);
    setMessages([]);
  };

  const openSession = async (sid) => {
    localStorage.setItem(LS_SESSION, sid);
    setSessionId(sid);
    const r = await api.get(`/brain/sessions/${sid}/messages`);
    setMessages(r.data || []);
  };

  const send = useCallback(async (text, endpoint = "ask") => {
    if (!text.trim() || sending) return;
    const q = text.trim();
    setMessages((m) => [...m, { role: "user", content: q }]);
    setInput("");
    setSending(true);
    try {
      const r = await api.post(`/brain/${endpoint}`, { message: q, session_id: sessionId });
      if (r.data.session_id !== sessionId) {
        localStorage.setItem(LS_SESSION, r.data.session_id);
        setSessionId(r.data.session_id);
      }
      setMessages((m) => [...m, { role: "assistant", content: r.data.answer }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry — couldn't reach the Brain just now." }]);
    } finally { setSending(false); }
  }, [sessionId, sending]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[440px] p-0 bg-surface border-l border-outline-variant flex flex-col"
        data-testid="brain-panel"
      >
        <SheetTitle className="sr-only">Ask the Brain</SheetTitle>
        <div className="relative overflow-hidden px-6 pt-6 pb-4 border-b border-outline-variant">
          <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-primary-container/60 blur-[70px] pointer-events-none" />
          <div className="relative flex items-center justify-between">
            <div className="inline-flex items-center gap-2 min-w-0">
              <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0">
                <Sparkles className="w-4 h-4" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-medium text-surface-on">Ask the Brain</div>
                <div className="text-xs text-surface-onVariant truncate">Grounded in your live data</div>
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Popover>
                <PopoverTrigger asChild>
                  <button data-testid="brain-history-btn" aria-label="History" className="p-2 rounded-full hover:bg-primary/10 text-surface-onVariant"><History className="w-4 h-4" /></button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-1 bg-surface rounded-2xl border border-outline-variant" align="end">
                  {sessions.length === 0 && <div className="px-3 py-4 text-xs text-surface-onVariant text-center">No past chats yet.</div>}
                  {sessions.map((s) => (
                    <button
                      key={s.session_id}
                      data-testid={`brain-history-${s.session_id.slice(-8)}`}
                      onClick={() => openSession(s.session_id)}
                      className="w-full text-left px-3 py-2 rounded-xl hover:bg-primary/8 transition-colors duration-200 ease-md"
                    >
                      <div className="text-sm text-surface-on truncate">{s.preview}</div>
                      <div className="text-[10px] text-surface-onVariant mt-0.5">
                        {s.count} messages · {new Date(s.last_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                      </div>
                    </button>
                  ))}
                </PopoverContent>
              </Popover>
              <button data-testid="brain-new-chat" onClick={newChat} aria-label="New chat" className="p-2 rounded-full hover:bg-primary/10 text-surface-onVariant"><MessageSquarePlus className="w-4 h-4" /></button>
              <button data-testid="brain-close" onClick={() => onOpenChange(false)} aria-label="Close" className="p-2 rounded-full hover:bg-primary/10 text-surface-onVariant"><X className="w-4 h-4" /></button>
            </div>
          </div>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 scrollbar-thin" data-testid="brain-messages">
          {messages.length === 0 && (
            <div className="text-center py-6">
              <div className="text-sm text-surface-onVariant">
                Hi {user?.name?.split(" ")[0]}. Ask me anything about your admin or finances.
              </div>
              <div className="mt-6 flex flex-wrap gap-2 justify-center">
                {SUGGESTED.map((s) => (
                  <button
                    key={s.label}
                    data-testid={`brain-chip-${s.label.slice(0, 20).replace(/\s+/g, "-").toLowerCase()}`}
                    onClick={() => send(s.label, s.endpoint)}
                    className={cn(
                      "text-xs px-3 py-2 rounded-full transition-colors duration-200 ease-md active:scale-95",
                      s.endpoint === "plan"
                        ? "bg-primary text-primary-foreground hover:brightness-110"
                        : "bg-surface-container hover:bg-primary-container text-surface-on"
                    )}
                  >{s.label}</button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={cn("flex", m.role === "user" ? "justify-end" : "justify-start")}>
              <div
                data-testid={`brain-msg-${m.role}-${i}`}
                className={cn(
                  "max-w-[85%] rounded-[20px] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap",
                  m.role === "user"
                    ? "bg-primary text-primary-foreground rounded-br-[6px]"
                    : "bg-surface-container text-surface-on rounded-bl-[6px]"
                )}
              >{m.content}</div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="bg-surface-container rounded-[20px] rounded-bl-[6px] px-4 py-3 text-sm text-surface-onVariant inline-flex items-center gap-2">
                <span className="inline-block w-2 h-2 rounded-full bg-primary animate-pulse" />
                <span className="inline-block w-2 h-2 rounded-full bg-primary animate-pulse" style={{ animationDelay: "150ms" }} />
                <span className="inline-block w-2 h-2 rounded-full bg-primary animate-pulse" style={{ animationDelay: "300ms" }} />
              </div>
            </div>
          )}
        </div>

        <form onSubmit={(e) => { e.preventDefault(); send(input); }} className="border-t border-outline-variant px-4 py-4 bg-surface">
          <div className="flex items-end gap-2 bg-surface-container rounded-[24px] px-4 py-2">
            <textarea
              data-testid="brain-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(input); } }}
              rows={1}
              placeholder="Ask about your items, money, day…"
              className="flex-1 bg-transparent outline-none resize-none text-sm text-surface-on placeholder:text-surface-onVariant py-2 max-h-32"
            />
            <button
              data-testid="brain-send"
              type="submit" disabled={!input.trim() || sending}
              aria-label="Send"
              className="shrink-0 w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center disabled:opacity-50 active:scale-95 hover:brightness-110 transition-[transform,filter] duration-200 ease-md"
            ><Send className="w-4 h-4" /></button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
